import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {NavController} from "@ionic/angular";
import {NavigationEnd, NavigationStart, Router} from "@angular/router";
import {UserService} from "../../services/user.service";
import {Article} from "../../structure/types/article.class";
import {StorageService} from "../../services/storage.service";
import {LangService} from "../../services/lang.service";
import {ContentService} from "../../services/content.service";
import {UtilityService} from "../../services/utility.service";
import {AppSettingsService} from "../../services/app-settings.service";
import {CrisisModeService} from "../../services/crisis-mode.service";

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    standalone: false
})
export class FooterComponent implements OnInit {

    @ViewChild('focusBack') focusBack!: ElementRef;

    showFooter = false;
    showGeneralFooterType = false;
    showBackButton = false;
    showSaveButton = false;
    showRemoveButton = false;
    previousUrl: string | undefined;
    currentUrl: string = '';
    savedArticles: Article[] = [];

    isDiscoverPage: boolean = false;
    isMoodLogPage: boolean = false;
    isProfilePage: boolean = false;
    isCrisisPage: boolean = false;
    isGuidesPage: boolean = false;
    crisisModeEnabled: boolean = true;
    crisisModeActive: boolean = false;

    user: any;

    private profileRoutes: string[] = [
        '/account',
        '/account/edit-account',
        '/account/change-password'
    ];

    private moodLogRoutes: string[] = [
        '/add-entry',
        '/calendar',
        '/not-logged-in'
    ];

    private discoverPageRoutes: string[] = [
        '/categories',
        '/home'
    ];

    private generalFooterRoutes: string[] = [
        '/calendar',
        '/home',
        '/not-logged-in',
        '/categories',
        '/mood-log',
        '/guides',
        '/guide',
        '/added-entry',
        '/account',
        '/account/edit-account',
        '/account/change-password',
        '/add-entry',
        '/articles',
        '/category',
        '/search',
        '/directory-of-services',
        '/crisis-mode'
    ]

    private crisisRoutes: string[] = ['/crisis-mode']

    private guidesRoutes: string[] = [
        '/guides',
        '/guide',
        '/search'
    ]

    private hiddenRoutes: string[] = [
        '/onboarding',
        '/splash',
        '/intro',
        '/2fa'
    ];

    private riskAssessmentIgnoreRoutes: string[] = [
        '/onboarding',
        '/splash',
        '/intro',
        '/2fa',
        '/login',
        '/register',
        '/forgot'
    ];

    constructor(
        private navController: NavController,
        private router: Router,
        private userService: UserService,
        private storageService: StorageService,
        public lang: LangService,
        private contentService: ContentService,
        private utilityService: UtilityService,
        private appSettingsService: AppSettingsService,
        private crisisModeService: CrisisModeService,
    ) {
        this.appSettingsService.load();
        this.appSettingsService.crisisModeEnabled$.subscribe((enabled) => {
            this.crisisModeEnabled = enabled;
        });

        // Works for guests too, so the tab stays highlighted while crisis mode is on.
        this.crisisModeService.load();
        this.crisisModeService.active$.subscribe((active) => {
            this.crisisModeActive = active;
            this.checkRoute(this.currentUrl);
        });
        this.router.events
            .subscribe((event) => {

                if (event instanceof NavigationStart) {
                    this.checkRoute(event.url);
                }

                if (event instanceof NavigationEnd) {
                    this.contentService.pageView(event.url);
                    this.previousUrl = this.currentUrl;
                    this.currentUrl = event.url;
                    this.checkRoute(event.url);
                }
            });

        this.userService.savedArticles.subscribe((savedArticles:any) => {
            this.savedArticles = savedArticles;
            this.checkRoute(this.currentUrl);
        })

        this.userService.data.subscribe((userData) => {
            this.user = userData;
            this.checkRoute(this.currentUrl);
        })
    }

    async ngOnInit() {
        const token = await this.storageService.get('token');
        if (null === token) return;
        await this.userService.getSavedArticlesData();
    }

    /**
     * Go Back Programmatically
     */
    goBack(): void {
        if (this.previousUrl === undefined || this.previousUrl === '/account/validate') {
            this.navController.navigateBack(['/home'])
        }

        this.focusBack.nativeElement.focus();
        this.navController.back();
    }

    /**
     * Go Home Screen
     */
    goHome(): void {
        this.navController.navigateRoot(['/home'])
    }

    /**
     * Go Guides Page
     */
    async goToGuidesPage() {
        let token = await this.storageService.get('token');
        let moodRoute = '/guides-not-logged-in';
        if (this.userService.isLoggedIn && token !== null) {
            moodRoute = '/guides'
        }
        await this.navController.navigateRoot([moodRoute]);
    }

    /**
     * Explore — opens the (blank) search page.
     */
    async goToExplore() {
        await this.navController.navigateRoot(['/search']);
    }


    /**
     * Go To Mood log or to Not Logged In Screen
     */
    async goToMoodLog() {
        let token = await this.storageService.get('token');
        let moodRoute = '/calendar-not-logged-in';
        if (this.userService.isLoggedIn && token !== null) {
            moodRoute = '/calendar'
        }
        await this.navController.navigateRoot([moodRoute]);
    }

    async goToProfile() {
        let token = await this.storageService.get('token');

        // dacă nu e logat -> login (sau /account-not-logged-in dacă ai)
        if (!this.userService.isLoggedIn || token === null) {
            await this.navController.navigateRoot(['/login']);
            return;
        }

        await this.navController.navigateRoot(['/account']);
    }

    /**
     * Crisis mode is available to everyone, guests included.
     */
    async goToCrisis() {
        await this.navController.navigateRoot(['/crisis-mode']);
    }

    async openLiveChat() {
        await this.utilityService.openExternalLink('https://livechat.victimsupport.org.uk/newchat/chat.aspx?domain=www.mysupportspace.org.uk')
    }

    /**
     * Check Current Route to set proper actions available
     * @param url
     * @private
     */
    private checkRoute(url: string) {
        if (url === "/") {
            this.showFooter = false;
            return;
        }

        if (this.user && this.user.do_risk_assessment) {

            const notIgnored = !this.riskAssessmentIgnoreRoutes.some(route => url.includes(route));
            const notOnRisk = !url.startsWith('/risk-assesment');

            if (notIgnored && notOnRisk) {
                this.navController.navigateRoot([`/risk-assesment/${this.user.tag}`]);
                return;
            }
        }


        this.showFooter = !this.hiddenRoutes.some(route => url.includes(route));
        this.showGeneralFooterType = this.generalFooterRoutes.some(route => url.includes(route));
        this.isDiscoverPage = this.discoverPageRoutes.some(route => url.includes(route));
        this.isMoodLogPage = this.moodLogRoutes.some(route => url.includes(route));
        this.isGuidesPage = this.guidesRoutes.some(route => url.includes(route));
        this.isProfilePage = this.profileRoutes.some(route => url.includes(route));
        this.isCrisisPage = this.crisisRoutes.some(route => url.includes(route)) || this.crisisModeActive;

        this.showBackButton = !url.includes('home');
        this.showSaveButton = url.includes('/article/');
        this.showRemoveButton = false;

        if (typeof this.savedArticles.filter !== 'function') {
            return
        }

        //@ts-ignore
        let isSavedArticle = this.savedArticles.filter((article: Article) => {
            return '/article/' + article.slug === url
        })

        if (isSavedArticle.length) {
            this.showSaveButton = false;
            this.showRemoveButton = true;
        }
    }


    saveArticle() {
        if (!this.userService.isLoggedIn) {
            this.navController.navigateRoot(['/login'])
        } else {
            this.userService.saveArticleEvent.emit();
            this.showSaveButton = false;
            this.showRemoveButton = true;
        }
    }


    removeArticle() {
        if (!this.userService.isLoggedIn) {
            this.navController.navigateRoot(['/login'])
        } else {
            this.userService.removeArticleEvent.emit();
            this.showSaveButton = true;
            this.showRemoveButton = false;
        }
    }

}
